import { useState, useEffect } from "react";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";
import { Label } from "../../../components/ui/label";
import { Input } from "../../../components/ui/input";
import { Edit2Icon } from "lucide-react";

const NON_EDITABLE_COLUMNS = new Set([
  "underlying",
  "strategy",
  "option_type",
  "symbol",
  "id",
  "created_at",
  "updated_at",
  "clientId",
]);

export function EditDynamicRegimeConfigModal({
  rowData,
  displayName,
  handleEditLotConfig,
}) {
  const [open, setOpen] = useState(false);
  const [lotConfig, setLotConfig] = useState({});

  useEffect(() => {
    if (rowData) {
      const initialConfig = {};
      Object.keys(rowData).forEach((key) => {
        if (!NON_EDITABLE_COLUMNS.has(key)) {
          initialConfig[key] = rowData[key] ?? 0;
        }
      });
      setLotConfig(initialConfig);
    }
  }, [rowData, open]);

  const handleUpdate = () => {
    const sanitizedConfig = {};
    Object.entries(lotConfig).forEach(([k, v]) => {
      sanitizedConfig[k] = v === "" || isNaN(Number(v)) ? 0 : Number(v);
    });
    handleEditLotConfig(rowData, sanitizedConfig);
    setOpen(false);
  };

  const handleChange = (key, value) => {
    setLotConfig((prev) => ({
      ...prev,
      [key]: value === "" ? "" : Number(value),
    }));
  };

  const identifiers = [
    rowData?.symbol,
    rowData?.underlying,
    rowData?.strategy,
    rowData?.option_type,
  ]
    .filter(Boolean)
    .join(" - ");

  const editableKeys = Object.keys(lotConfig).sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-zinc-800 text-white px-2.5 hover:bg-zinc-700 rounded-[5px]">
          <Edit2Icon className="w-4 h-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[750px] max-h-[85vh] overflow-y-auto border-zinc-700">
        <DialogHeader>
          <DialogTitle className="text-white">
            Edit {displayName || "Regime"} Config {identifiers ? `(${identifiers})` : ""}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-4 py-4 max-h-[60vh] overflow-y-auto custom-scroll pr-2">
          {editableKeys.map((key) => (
            <div key={key} className="grid grid-cols-1 items-center gap-1.5">
              <Label className="text-white font-medium text-xs">{key.toUpperCase()}</Label>
              <Input
                type="number"
                placeholder={`Enter ${key}`}
                className="text-white bg-zinc-900 border-zinc-700 h-9"
                value={lotConfig[key] ?? ""}
                onChange={(e) => handleChange(key, e.target.value)}
              />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button
            type="submit"
            className="text-[15px] font-normal bg-zinc-800 text-white hover:bg-zinc-700"
            onClick={handleUpdate}
          >
            Update {displayName || "Regime"} Config
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
